import React, { useContext, useState } from "react";
import {
  Box,
  Divider,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { Payments } from "@mui/icons-material";
import { MainButton, SecondaryButton } from "../global-components/MainButton";
import BmAlert from "../global-components/BM_Alert";
import { BookingContext } from "../bookingContext/BookingContex";
import { submitBookingAndCheckoutService } from "../bookingServices";
import { calculateAge } from "../services/timeService";
import moment from "moment-timezone";
import { showGlobalAlert } from "../bookingUtils/globalAlert";

const Step3Summary = () => {
  const {
    bookingPerson,
    patient,
    selectedAptData,
    userTimeZone,
    doctorDetails,
    isAuthenticated,
    contactDetails,
    prevStep,
  } = useContext(BookingContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // HSP implementation
  const providerName =
    doctorDetails?.displayName ||
    [doctorDetails?.prefix, doctorDetails?.name].filter(Boolean).join(" ") ||
    doctorDetails?.doctorName ||
    "Unknown health professional";

  const providerLabel = doctorDetails?.professionType || "Health professional";

  // --- Contact source ---
  const contactEmail =
    contactDetails.verifiedEmail || contactDetails.enteredEmail;
  const contactPhone =
    contactDetails.verifiedPhone || contactDetails.enteredPhone;

  const finalFee = doctorDetails?.displayingFee || 0;
  const doctorName =
    doctorDetails?.doctorName || doctorDetails?.name || "Unknown Doctor";
  const localDisplayTime = selectedAptData?.localTimeDisplay || "";

  // --- Final confirmation ---
  const handleFinalConfirm = async () => {
    setIsSubmitting(true);
    setErrorMsg(null);

    const bookingState = {
      contactPhone,
      contactEmail,
      authStatus: bookingPerson.authStatus,
      authPatient: bookingPerson,
      appointmentPatient: patient,
      guestUser: {
        firstName: bookingPerson?.name?.firstName || "",
        lastName: bookingPerson?.name?.lastName || "",
        email: contactEmail,
        phoneNumber: contactPhone,
      },
    };

    const utcString = selectedAptData?.slotData?.timeUtc;
    // const aptDate = utcString.split("T")[0];
    const aptDate = moment.utc(utcString).format("YYYY-MM-DD");

    const appointmentDetails = {
      doctorId: doctorDetails?.doctorId,
      doctorName: doctorName,
      displayingFee: finalFee,
      selectedDate: aptDate,
      selectedTimeSlot: selectedAptData?.slotData?.timeUtc,
      selectedTimeZone: userTimeZone,
    };

    try {
      // alert("Booking Details : " + JSON.stringify(bookingState));
      // alert("Appointment Details : " + JSON.stringify(appointmentDetails));

      const result = await submitBookingAndCheckoutService(
        bookingState,
        appointmentDetails,
      );

      if ("recovered" in result && result.recovered) {
        showGlobalAlert(
          "info",
          "alert",
          "You’re already registered. Please confirm your details again.",
        );
        setIsSubmitting(false);
        prevStep();
        return;
      }

      if (result.success) {
        // console.log("Booking completed successfully");
        // handle payment redirect or success message here
      } else {
        setErrorMsg(result.message || "Booking failed to finalize.");
      }
    } catch (error) {
      console.error("Booking and Checkout Error:", error);
      setErrorMsg(
        error.message ||
          "An unexpected error occurred during payment initiation.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Render ---
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, mb: 3, maxWidth: "md", mx: "auto" }}>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle1" color="info.main" mb={2}>
        Summary & Payment
      </Typography>

      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 2,
          bgcolor: "grey.50",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 2, md: 4 }}
          justifyContent="space-between"
        >
          {/* --- Patient Details --- */}
          <Box flex={1}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              mb={1}
              color="primary.main"
            >
              Patient Details
            </Typography>

            <Grid container spacing={0.5}>
              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Name:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {patient.firstName} {patient.lastName}
                </Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Age:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {calculateAge(patient.dob) || patient.birthDate}
                </Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Gender:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {patient.gender || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Address:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {patient.address || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Contact:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {contactEmail} / {contactPhone}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* --- Appointment Details --- */}
          <Box flex={1}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              mb={1}
              color="primary.main"
            >
              Appointment Details
            </Typography>

            <Grid container spacing={0.5}>
              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Provider:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                {/* <Typography variant="body2">Dr. {doctorName}</Typography> */}
                <Typography variant="body2">{providerName}</Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Profession:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                {/* <Typography variant="body2">Dr. {doctorName}</Typography> */}
                <Typography variant="body2" >
  {providerLabel}
</Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Date:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {selectedAptData?.localDateDisplay}
                </Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Time:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {localDisplayTime} ({userTimeZone})
                </Typography>
              </Grid>

              <Grid item xs={5} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Fee:
                </Typography>
              </Grid>
              <Grid item xs={7} sm={8}>
                <Typography variant="body2">
                  {finalFee.toFixed(2)} LKR
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Box>

      {/* --- Alerts & Buttons --- */}
      <Box sx={{ my: 2 }}>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}
        <BmAlert
          severity="success"
          title={`Total Fee: ${finalFee.toFixed(2)} LKR`}
          message="Ready to proceed to payment"
        />
      </Box>

      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <SecondaryButton
          onClick={prevStep}
          btnLabel="Back"
          fullWidth
          disabled={isSubmitting}
        />
        <MainButton
          onClick={handleFinalConfirm}
          btnLabel={isSubmitting ? "Redirecting..." : "Confirm Booking & Pay"}
          icon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 0.5 }} />
            ) : (
              <Payments sx={{ mr: 0.5 }} />
            )
          }
          fullWidth
          disabled={isSubmitting}
        />
      </Stack>
    </Box>
  );
};

export default Step3Summary;
